import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useRevalidator } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import {
  setBrowseLocationId,
  useBrowseLocationId,
  useIsAnonymousBrowsing,
} from "@/modules/anonymousBrowsing";
import { getPlatformLocations } from "api/methods";
import { ModalSelectorList } from "organisms/selection-modals/modal-selector-list/ModalSelectorList";

import { useGuestLocationSuggestion } from "@/modules/anonymousBrowsing/useGuestLocationSuggestion";

import { invalidateShopForLocationChange } from "modules/platformLocation";

/**
 * Asks a visitor which location they are shopping, the first time it matters.
 *
 * Everything a logged-out visitor is shown is scoped to a location — which listings exist, what they
 * cost, which offers are running — and with no account there is nothing to read a home location from.
 * The resolver falls back to the practice's earliest-created active location, which is a reasonable
 * default and a poor guess: at a multi-location practice it silently shows one branch's catalogue and
 * prices to someone who may want another. A signed-in patient is asked this at sign-up
 * (`/signin/location`); this is the same question for someone who has not signed up yet.
 *
 * Shown once, and only where the answer changes something: anonymous browsing active, the practice
 * has more than one active location, and the visitor has not already chosen. Geolocation can suggest
 * a branch when its proximity is unambiguous; failing that, the server's IP-based nearest branch is
 * preselected instead. Either way the visitor must confirm before continuing, and with neither the
 * visitor selects manually. The shop banner lets them change it later.
 */
export const AnonymousLocationPrompt = () => {
  const { t } = useTranslation();
  const { revalidate } = useRevalidator();
  const isAnonymousBrowsing = useIsAnonymousBrowsing();
  const chosenLocationId = useBrowseLocationId();
  const needsAnswer = isAnonymousBrowsing && !chosenLocationId;

  const { data: locations } = useQuery({
    ...getPlatformLocations.keyed({}),
    select: ({ items }) => items,
    enabled: needsAnswer,
  });
  const [manuallySelectedLocationId, setManuallySelectedLocationId] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const { data: suggestion } = useGuestLocationSuggestion(
    locations ? locations.map((location) => location.id) : [],
    needsAnswer && !manuallySelectedLocationId,
  );
  // A result arriving after the visitor clicks a branch must never replace that choice.
  const selectedLocationId = manuallySelectedLocationId || suggestion?.locationId;

  if (!needsAnswer || !locations || locations.length <= 1) {
    return null;
  }

  const accept = async () => {
    if (!selectedLocationId) return;

    setIsUpdating(true);
    try {
      // Set the request header before invalidation refetches location-scoped data.
      setBrowseLocationId(selectedLocationId);
      await invalidateShopForLocationChange();
      // Shop content comes from route loaders rather than these queries, so the loaders have to re-run
      // for the answer to reach the page.
      revalidate();
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <ModalSelectorList
      size="medium"
      open
      title={t("anonymousLocationPrompt.title")}
      type="location"
      items={locations.map((location) => ({
        id: location.id,
        title: location.name ?? "",
        description: `${location.city}, ${location.state}, ${location.postalCode}`,
        subtitle: location.address1 ?? undefined,
        tagText:
          suggestion?.locationId === location.id
            ? t(
                suggestion.source === "precise"
                  ? "anonymousLocationPrompt.youAreHere"
                  : "anonymousLocationPrompt.closestToYou",
              )
            : undefined,
      }))}
      checkedItems={selectedLocationId ? [selectedLocationId] : []}
      firstButtonLabel={t("anonymousLocationPrompt.shopThisLocation")}
      firstButtonDisabled={!selectedLocationId || isUpdating}
      onFirstButtonClick={() => void accept()}
      onSelect={setManuallySelectedLocationId}
    />
  );
};
